import { AssetCatalog, ConfigEditor } from "src/Modules";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Arbitrage } from "src/Modules/Arbitrage";
import { PredictionsModule } from "src/Modules/Predictions";
import { useAuth0 } from "@auth0/auth0-react";
import { LinearProgress } from "@mui/material";
import { Login } from "src/Modules/Login";
import { useEffect } from "react";
import { toast } from "react-toastify";
import { Layout } from "src/Common";
import { AUTH_DISABLED } from "./Providers";

function getDefaultRoute(): string {
  const storedMode = localStorage.getItem("app_mode");
  if (storedMode === "predictions") return "/predictions/opportunities";
  return "/arbitrage";
}

// Main app routes (used when authenticated or auth disabled)
const AuthenticatedApp = () => (
  <BrowserRouter>
    <Layout>
      <Routes>
        <Route path="arbitrage/*" element={<Arbitrage />} />
        <Route path="predictions/*" element={<PredictionsModule />} />
        <Route path="config-editor/*" element={<ConfigEditor />} />
        <Route path="assets" element={<AssetCatalog />} />
        {/* Redirects from old paths */}
        <Route
          path="prediction-mapper"
          element={<Navigate replace to="/predictions/mapper" />}
        />
        <Route
          path="arbitrage/predictions"
          element={<Navigate replace to="/predictions/opportunities" />}
        />
        <Route
          path="arbitrage/predictions/*"
          element={<Navigate replace to="/predictions/opportunities" />}
        />
        <Route
          path="arbitrage/predictions-status"
          element={<Navigate replace to="/predictions/status" />}
        />
        <Route path="/" element={<Navigate replace to={getDefaultRoute()} />} />
      </Routes>
    </Layout>
  </BrowserRouter>
);

// Router with Auth0 (production)
const AuthenticatedRouter = () => {
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    const getAccessToken = async () => {
      try {
        const accessToken = await getAccessTokenSilently();
        localStorage.setItem(import.meta.env.VITE_AUTH_TOKEN_KEY!, accessToken);
      } catch (error: any) {
        toast(`Auth Error! ${error.message}`, {
          theme: "dark",
          type: "error",
          autoClose: 8000,
          pauseOnFocusLoss: false,
        });
      }
    };

    isAuthenticated && getAccessToken();
  }, [isAuthenticated, getAccessTokenSilently]);

  if (isLoading) return <LinearProgress />;

  return isAuthenticated ? <AuthenticatedApp /> : <Login />;
};

// Main router - skips Auth0 completely when disabled
export const AppRouter = () => {
  // When auth is disabled, render app directly without Auth0
  if (AUTH_DISABLED) {
    return <AuthenticatedApp />;
  }

  return <AuthenticatedRouter />;
};
